import { useEffect, useState, useSyncExternalStore } from "react";
import type { StepView } from "@/app/queries";
import type { TocItem } from "@/ui/Toc";
import { plural } from "@/lib/plural";

/* Чистые вычисления экрана шага: все счётчики — из данных StepView. */

export type TickKind = "unit" | "task" | "resource" | "quiz" | "extra";
export type TickVars = { kind: TickKind; key: string; on: boolean };
export type TickFn = (vars: TickVars) => void;

export type Resource = StepView["content"]["resources"][number];
export type Task = StepView["content"]["practice"]["base"][number];
export type Criterion = StepView["gate"]["criteria"][number];
export type QuizItem = { id: string; q: string; a: string };

export type ResourceProgress = {
  res: Resource;
  /** Отмечено юнитов без ⏭️ (у ресурса без гайда — 1, если отмечен целиком). */
  done: number;
  /** Юнитов всего без ⏭️ (у ресурса без гайда — 1). */
  total: number;
  /** ⏭️-юнитов «можно пропустить» — вне счётчика и вне гейта. */
  skipped: number;
  noGuide: boolean;
  wholeTicked: boolean;
  mark: "✓" | "▸" | "○";
  /** Подпись справа: «3/4 юнитов», «просмотрено», «не начат». */
  frac: string;
};

export function resourceProgress(res: Resource, ticks: StepView["ticks"]): ResourceProgress {
  const units = new Set(ticks.units);
  const noGuide = res.units.length === 0;
  const wholeTicked = ticks.resources.includes(res.id);
  const counted = res.units.filter((u) => !u.skip);
  const total = noGuide ? 1 : counted.length;
  const done = noGuide ? (wholeTicked ? 1 : 0) : counted.filter((u) => units.has(u.key)).length;
  const mark = done === total ? "✓" : done > 0 ? "▸" : "○";
  const frac = noGuide ? (wholeTicked ? "просмотрено" : "не начат") : `${done}/${total} юнитов`;
  return { res, done, total, skipped: res.units.length - counted.length, noGuide, wholeTicked, mark, frac };
}

/** Ресурсы, которые считает гейт (без «Первоисточника»). */
export function gateResources(view: StepView): Resource[] {
  return view.content.resources.filter((r) => !r.optional);
}

/** Необязательные ресурсы — блок «Первоисточник», вне гейта. */
export function optionalResources(view: StepView): Resource[] {
  return view.content.resources.filter((r) => r.optional);
}

/** «2026-09-28» → «09.2026»; кривая дата — пусто. */
export function monthYear(date: string | undefined): string {
  const m = /^(\d{4})-(\d{2})-\d{2}/.exec(date ?? "");
  return m ? `${m[2]}.${m[1]}` : "";
}

/** Строка метаданных ресурса: meta · оценка (если её ещё нет в meta) · «актуально на ММ.ГГГГ». */
export function metaLine(r: Resource): string {
  const parts = [r.meta];
  const est = r.estimate?.trim();
  if (est && !r.meta.includes(est)) parts.push(est);
  const checked = monthYear(r.checked_on);
  if (checked) parts.push(`актуально на ${checked}`);
  return parts.filter(Boolean).join(" · ");
}

/** Вопросы «Проверь себя»: items; старый документ без items — строки text без ответа и без id. */
export function quizItems(view: StepView): QuizItem[] {
  const b = view.content.blocks.quiz;
  if (b?.items && b.items.length > 0) return b.items.filter((it) => it.q.trim());
  return lines(b?.text).map((q) => ({ id: "", q, a: "" }));
}

export const MARK_COLOR: Record<ResourceProgress["mark"], string> = {
  "✓": "var(--ok)",
  "▸": "var(--acc)",
  "○": "var(--mut)",
};

/** Строки блока: текст блока делится по переводам строк, пустые — вон. */
export function lines(text: string | undefined): string[] {
  return (text ?? "")
    .split("\n")
    .map((s) => s.trim())
    .filter(Boolean);
}

/** «Не проверил dtypes — числовой столбец…» → заголовок и пояснение по первому « — ». */
export function splitMistake(line: string): { t: string; d: string } {
  const i = line.indexOf(" — ");
  if (i < 0) return { t: line, d: "" };
  return { t: line.slice(0, i), d: line.slice(i + 3) };
}

export function blockOn(view: StepView, key: string): boolean {
  const b = view.content.blocks[key];
  return Boolean(b && b.on && (b.text.trim() || (b.items ?? []).some((it) => it.q.trim())));
}

/** Порог практики в процентах — из критерия practice_share, иначе 80. */
export function practiceThreshold(view: StepView): number {
  const cr = view.content.gate.criteria.find((c) => c.kind === "practice_share");
  return Math.round((cr?.threshold ?? 0.8) * 100);
}

/** Сколько задач из списка отмечено: по отметкам шага (задачи гейта) или по готовому набору ключей. */
export function tasksDone(tasks: Task[], ticks: StepView["ticks"] | Set<string>): number {
  const set = ticks instanceof Set ? ticks : new Set(ticks.tasks);
  return tasks.filter((t) => set.has(t.id)).length;
}

/** Все ресурсы гейта отмечены: юниты гайда без ⏭️ и ресурсы без гайда целиком. */
export function resourcesDone(view: StepView): boolean {
  return gateResources(view).every((r) => {
    const p = resourceProgress(r, view.ticks);
    return p.done === p.total;
  });
}

export type Section = "res" | "primary" | "quiz" | "mistakes" | "code" | "notes" | "practice" | "stuck" | "gate";

/** Оглавление S4: только непустые разделы, счётчики из данных. */
export function tocItems(view: StepView, active: Section): TocItem[] {
  const g = view.gate;
  const items: TocItem[] = [];
  const push = (id: Section, label: string, count: string, done = false) => items.push({ href: `#${id}`, label, count, done, active: active === id });
  push("res", "Ресурсы", `${g.units_done}/${g.units_total}`, resourcesDone(view));
  const opt = optionalResources(view).length;
  if (opt > 0) push("primary", "Первоисточник", String(opt));
  if (blockOn(view, "quiz")) push("quiz", "Проверь себя", String(quizItems(view).length));
  if (blockOn(view, "mistakes")) push("mistakes", "Типичные ошибки", String(lines(view.content.blocks.mistakes?.text).length));
  if (blockOn(view, "code")) push("code", "Пример кода", "1");
  if (blockOn(view, "notes")) push("notes", "Заметки менторов", String(lines(view.content.blocks.notes?.text).length));
  if (g.tasks_total > 0 || view.content.practice.extra.length > 0) push("practice", "Практика", `${g.tasks_done}/${g.tasks_total}`, g.tasks_total > 0 && g.tasks_done === g.tasks_total);
  if (view.content.stuck.on) push("stuck", "Если застрял", "");
  push("gate", "Готов к переходу", `${g.met}/${g.total}`, view.gate_status === "passed");
  return items;
}

export const GATE_BADGE: Record<StepView["gate_status"], { label: string; tone: "warn" | "ok" }> = {
  open: { label: "гейт не сдан", tone: "warn" },
  submitted: { label: "ждёт ментора", tone: "warn" },
  passed: { label: "гейт сдан", tone: "ok" },
};

/** Причина рядом с кнопкой «сдать гейт» — всегда есть (правило 3). */
export function submitReason(view: StepView): string {
  if (view.gate_status === "submitted") return "ждёт ментора";
  if (view.gate_status === "passed") return "гейт сдан";
  return view.gate.reason || "все критерии выполнены";
}

export function canSubmit(view: StepView): boolean {
  return view.gate_status === "open" && view.gate.can_submit;
}

export function criteriaLabel(total: number): string {
  return `${total} ${plural(total, "критерий", "критерия", "критериев")}`;
}

/** Первый неотмеченный элемент шага — куда ведёт «продолжить шаг» (S8). */
export function nextUntickedTarget(view: StepView): { tab: "res" | "practice" | "gate"; id: string | null } {
  const units = new Set(view.ticks.units);
  for (const r of gateResources(view)) {
    if (r.units.length === 0) {
      if (!view.ticks.resources.includes(r.id)) return { tab: "res", id: `resource-${r.id}` };
      continue;
    }
    const u = r.units.find((x) => !x.skip && !units.has(x.key));
    if (u) return { tab: "res", id: `unit-${u.key}` };
  }
  const tasks = new Set(view.ticks.tasks);
  const t = [...view.content.practice.base, ...view.content.practice.apply].find((x) => !tasks.has(x.id));
  if (t) return { tab: "practice", id: `task-${t.id}` };
  return { tab: "gate", id: "gate" };
}

/** Текст ошибки API без зависимости от класса (в тестах модуль запросов замокан). */
export function errorInfo(err: unknown): { status: number; code: string; message: string } {
  const e = err as { status?: unknown; code?: unknown; message?: unknown } | null;
  return {
    status: typeof e?.status === "number" ? e.status : 0,
    code: typeof e?.code === "string" ? e.code : "unknown",
    message: typeof e?.message === "string" && e.message ? e.message : "Не удалось выполнить запрос.",
  };
}

/* ── хуки раскладки ─────────────────────────────────────────── */

const MOBILE = "(max-width: 767.98px)";

function subscribe(cb: () => void) {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return () => {};
  const mq = window.matchMedia(MOBILE);
  mq.addEventListener("change", cb);
  return () => mq.removeEventListener("change", cb);
}

function snapshot() {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  return window.matchMedia(MOBILE).matches;
}

/** < md — мобильная раскладка S8; без matchMedia (jsdom) — десктоп. */
export function useIsMobile(): boolean {
  return useSyncExternalStore(subscribe, snapshot, () => false);
}

/** Активный раздел оглавления по прокрутке; без IntersectionObserver — первый.
 *  ready — разделы уже в DOM (шаг загружен, десктопная раскладка). */
export function useActiveSection(ids: Section[], ready: boolean, initial: Section = "res"): Section {
  const [active, setActive] = useState<Section>(initial);
  useEffect(() => {
    if (!ready || typeof IntersectionObserver === "undefined") return;
    const visible = new Map<Section, number>();
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          const id = e.target.id as Section;
          if (e.isIntersecting) visible.set(id, e.boundingClientRect.top);
          else visible.delete(id);
        }
        if (visible.size === 0) return;
        const top = [...visible.entries()].sort((a, b) => a[1] - b[1])[0];
        if (top) setActive(top[0]);
      },
      { rootMargin: "-80px 0px -60% 0px" },
    );
    for (const id of ids) {
      const el = document.getElementById(id);
      if (el) io.observe(el);
    }
    return () => io.disconnect();
  }, [ready, ids.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps
  return active;
}
